import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Popper,
  TextField,
} from '@mui/material';

import { selectUser } from 'entities/user/selectors';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { white } from 'utils/color-styles';
import { useDebounce } from 'utils/useDebounce';
import Organization from 'entities/organization/model';
import { PopulatedRoleGrant } from 'entities/user/model';
import { truthy } from 'utils/truthy';

import { searchThreshold, useOrganizations } from '../../entities/organization/useOrganizations';

import { switchOrganizationAction } from './actions';
import epics from './epics';
import reducer from './reducer';
import { selectUpdating } from './selectors';
import { renderOption } from './renderOption';

const dropdownWidth = 460;

const formatEIN = (input: string): string => {
  if (/^\d{2}-\d{7}$/.test(input.trim())) {
    return input.replace(/-/g, '');
  }
  return input;
};

/**
 * There are far fewer companies than nonprofits, therefore it makes sense to have them always come first
 */
const sortCompaniesFirst = (a: Organization, b: Organization) => {
  if (a.type === 'Company' && b.type !== 'Company') {
    return -1;
  }
  if (b.type === 'Company' && a.type !== 'Company') {
    return 1;
  }
  return 0;
};

export const OrganizationSelector = () => {
  useInjectEpics({ key: 'organizationSelector', epics });
  useInjectReducer({ key: 'organizationSelector', reducer });
  const [width, setWidth] = useState(160);
  const [inputFocused, setInputFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isUserAdmin = user?.type === 'Admin';

  const hasGlobalOrganizationRoleGrant = (user?.roleGrants || []).some(
    (roleGrant) => roleGrant.enabled && roleGrant.context?.organizationTypes?.length > 0
  );
  const globalSearch = isUserAdmin || hasGlobalOrganizationRoleGrant;
  const grantedOrganizations = Array.from(
    new Set((user?.roleGrants || []).map((roleGrant) => roleGrant?.context?.organization?.id))
  );
  const hasGrants = (grantedOrganizations?.length || 0) > 1;
  const isUpdating = useSelector(selectUpdating);

  const { data, loading, error } = useOrganizations(formatEIN(debouncedSearchTerm), !globalSearch);

  useEffect(() => {
    if (ref.current) {
      setWidth(ref.current.offsetWidth);
    }
  }, [user?.organization.name]);

  if (!globalSearch && !hasGrants) {
    return null;
  }

  if (!user) {
    return null;
  }

  if (isUpdating) {
    // Prevent the user from doing anything while swithcing the organization
    return createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: white,
          zIndex: 2147483647,
        }}
      >
        <LinearProgress />
      </div>,
      document.body
    );
  }

  const getOptions = () => {
    if (globalSearch) {
      if (data?.length > 0) {
        return data.sort(sortCompaniesFirst);
      }
      // @NOTE-DP: sibling subsidiaries are used as the default option for quick access
      return user?.organization?.siblingSubsidiaries || [];
    }
    // @NOTE-DP: for regular users we show switch options based on role grants
    return (
      (user?.roleGrants.map((roleGrant) => roleGrant.context.organization) || [])
        .filter(truthy)
        // @NOTE-DP: A user may have multiple role grants for the same organization. But we should only display it once.
        .reduce<PopulatedRoleGrant['context']['organization'][]>(
          (organizations, currentOrganization) => {
            if (!organizations.find((organization) => currentOrganization.id === organization.id)) {
              organizations.push(currentOrganization);
            }
            return organizations;
          },
          []
        )
    );
  };

  return (
    <>
      {/* We need to calculate the desired width of the input. We render an invisible element using the same font and measure its width. */}
      <div
        ref={ref}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 0,
          fontFamily: 'Graphik',
          // @NOTE-DP: this number comes from MuiInputBase somewhere
          fontSize: `${6 / 7}rem`,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {user?.organization.name}
      </div>
      <Autocomplete
        options={getOptions()}
        noOptionsText={
          searchTerm.length > searchThreshold
            ? 'No matching organizations found'
            : 'Search by organization name, EIN, or email'
        }
        onChange={(_e, value) => {
          if (value?.id) {
            dispatch(switchOrganizationAction(value?.id));
          }
        }}
        getOptionLabel={(option) => option.name || ''}
        filterOptions={
          globalSearch
            ? // @NOTE-DP: for admins, we need to disable client-side filtering because we do proper server search
              (o) => o
            : // @NOTE-DP: for regular users, the client-side filtering should work
              undefined
        }
        value={user?.organization}
        renderOption={(props, option, state) =>
          renderOption(props, option, { ...state, isUserAdmin })
        }
        clearIcon={null}
        PopperComponent={(props) => (
          <Popper {...props} style={{ width: dropdownWidth }}>
            {loading && globalSearch ? (
              <Paper>
                <Box p={2}>
                  <CircularProgress size={24} />
                </Box>
              </Paper>
            ) : (
              props.children
            )}
          </Popper>
        )}
        renderInput={(params) => (
          <TextField
            variant="standard"
            {...params}
            inputProps={{
              ...params.inputProps,
              value: inputFocused ? searchTerm : user.organization?.name,
              onChange: (e) => {
                setSearchTerm(e.target.value);
                params.inputProps?.onChange?.(e);
              },
              onFocus: (e) => {
                setInputFocused(true);
                params.inputProps?.onFocus?.(e);
              },
              onBlur: (e) => {
                setInputFocused(false);
                params.inputProps?.onBlur?.(e);
              },
            }}
            sx={{
              width:
                !inputFocused && user.organization?.name?.length > 0
                  ? // Dynamically adjust to the organization name length
                    Math.min(width + 26, dropdownWidth)
                  : dropdownWidth,
              '& .v5-MuiInput-underline:before': {
                borderBottom: inputFocused ? undefined : '1px transparent',
              },
              '& .v5-MuiAutocomplete-inputRoot': {
                paddingRight: '20px !important',
              },
            }}
          />
        )}
        size="small"
        sx={{
          marginLeft: 2,
        }}
      />
      {error && (
        <Alert severity="error" sx={{ marginLeft: 2 }}>
          Failed to load data
        </Alert>
      )}
    </>
  );
};
